import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Which groups the visitor has opened or closed, and whether the mobile panel is open.
 *
 * Re-rendering the section hands back the server's default state, which would shut the dropdown
 * the visitor just used. Live keeps each group as it was found, so the choices are held here for
 * the life of the page — a real navigation resets them, as it does on live. Groups the visitor
 * has not touched are absent, so the server's own default still applies to them.
 */
const openGroups = new Map();
/** @type {boolean | null} */
let panelOpen = null;

/** `data-group-key` of the sort menu, which is excluded from the state above. */
const SORT_GROUP_KEY = 'sort';

/**
 * Tag filtering for the collection grid, without a page load.
 *
 * The links are real anchors carrying real tag URLs, so filtering still works with this script
 * blocked, still opens in a new tab on ctrl/cmd-click, and is crawlable. When the script is
 * running, a plain left click is intercepted instead: the URL is pushed onto history and the
 * section is re-rendered in place, which is the same pair of steps Horizon's own `facets.js`
 * performs in `updateFiltersByURL`.
 *
 * The same class drives the sidebar and the top strip; the toggle handlers simply never fire on
 * the strip, which has nothing to open.
 */
class EqCollectionFilter extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);
    window.addEventListener('popstate', this.#handlePopState);

    // Covers the re-render replacing this node outright; when it only patches attributes instead,
    // `#renderSection` calls the same method on the surviving element.
    this.restoreOpenState();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('popstate', this.#handlePopState);
    this.#stopWatchingForDismiss();

    super.disconnectedCallback();
  }

  /**
   * The id of the section to re-render, read from the `results-list` this filter sits in.
   *
   * @returns {string | null}
   */
  get sectionId() {
    return this.closest('results-list')?.getAttribute('section-id') ?? null;
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;

    // Anything the visitor meant as a real navigation is left alone: modified clicks, middle
    // clicks, and links asking for another tab.
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target && link.target !== '_self') return;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return;

    // No section to re-render means no in-place update is possible, so let the browser navigate.
    const { sectionId } = this;
    if (!sectionId) return;

    // Live Nikura collection interaction contract, using the SAME existing
    // Horizon filter click (no duplicate delegate or legacy navigation handler).
    this.#trackCollectionFilter(link, url);
    event.preventDefault();

    history.pushState({ eqCollectionFilter: true }, '', url.toString());
    this.#renderSection(sectionId, url);
  };

  /** @param {HTMLAnchorElement} link @param {URL} url */
  #trackCollectionFilter(link, url) {
    const collectionName = this.dataset.eqCollectionName || document.querySelector('.eq-collection-grid')?.dataset.eqCollectionName || '';
    const active = link.getAttribute('aria-current') === 'true' || link.classList.contains('eq-collection-filter__option--active') || link.classList.contains('eq-collection-top-filter__link--active');
    const tab = document.querySelector('.eq-collection-top-filter__link--active')?.textContent?.trim() || '';
    const group = link.closest('.eq-collection-filter__group');
    const filterType = group?.querySelector('.eq-collection-filter__group-toggle')?.textContent?.trim() || '';
    const text = link.textContent?.trim() || '';
    // The link itself distinguishes the sort dropdown from tag URLs. A filter URL
    // can retain an existing sort_by query parameter, so inspecting the resolved
    // URL would incorrectly report filter clicks as sorting.
    const isSortLink = (link.getAttribute('href') || '').startsWith('?sort_by=');
    const eventName = isSortLink ? 'collection_sort_change' : active ? 'collection_filter_remove' : 'collection_filter_apply';
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eventName === 'collection_sort_change'
      ? { event: eventName, collection_name: collectionName, text, tab }
      : { event: eventName, filter_type: filterType, collection_name: collectionName, text, tab });
  }

  /**
   * Back and forward have to re-render too, or the URL and the grid fall out of step.
   */
  #handlePopState = () => {
    const { sectionId } = this;
    if (!sectionId) return;

    // The sidebar and the top strip share one section; only the first renders it, or Back fires two renders.
    const owner = this.closest('results-list')?.querySelector('eq-collection-filter, eq-collection-top-filter');
    if (owner && owner !== this) return;

    this.#renderSection(sectionId, new URL(window.location.href));
  };

  /**
   * @param {string} sectionId
   * @param {URL} url
   */
  #renderSection(sectionId, url) {
    this.setAttribute('aria-busy', 'true');

    sectionRenderer
      .renderSection(sectionId, { url })
      .catch(() => {
        // The section could not be re-rendered — fall back to a real navigation rather than
        // leaving the URL pointing at results the visitor cannot see.
        window.location.assign(url.toString());
      })
      .finally(() => {
        this.removeAttribute('aria-busy');

        const current = this.isConnected
          ? this
          : document.querySelector(`results-list[section-id="${sectionId}"] ${this.localName}`);

        if (current instanceof EqCollectionFilter) current.restoreOpenState();
      });
  }

  /**
   * Puts the groups and the panel back the way the visitor left them, after the section has been
   * re-rendered from the server's defaults.
   */
  restoreOpenState() {
    for (const button of this.querySelectorAll('.eq-collection-filter__group-toggle')) {
      const key = button instanceof HTMLElement ? button.dataset.groupKey : undefined;
      if (!key || !openGroups.has(key)) continue;

      button.setAttribute('aria-expanded', String(openGroups.get(key)));
    }

    if (panelOpen !== null) this.#setPanelExpanded(panelOpen);
  }

  /**
   * Toggles the whole panel. Mobile only; on desktop the panel is always laid out open and the
   * toggle button is hidden, so the attribute is harmless there.
   */
  togglePanel() {
    const { toggle } = this.refs;
    if (!(toggle instanceof HTMLElement)) return;

    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    this.#setPanelExpanded(!isExpanded);
  }

  /**
   * @param {boolean} expanded
   */
  #setPanelExpanded(expanded) {
    const { toggle } = this.refs;
    if (!(toggle instanceof HTMLElement)) return;

    toggle.setAttribute('aria-expanded', String(expanded));
    panelOpen = expanded;

    if (expanded) this.#startWatchingForDismiss();
    else this.#stopWatchingForDismiss();
  }

  /**
   * An open panel closes on a click anywhere outside it, and on Escape — the two dismissals a
   * visitor expects of a dropdown. The listeners only exist while it is open, so a page full of
   * closed filters costs nothing.
   *
   * `pointerdown` rather than `click`: it fires before the panel can move under the cursor, and
   * before a link inside the grid begins navigating.
   *
   * "Inside" is the toggle and the panel, not the whole component. The open panel covers most of
   * a phone screen, so the component also paints a full-viewport scrim to catch the taps that
   * land on the covered page; that scrim is a pseudo-element of the component, and a
   * `this.contains(target)` test would count it as inside and never dismiss.
   */
  #startWatchingForDismiss() {
    document.addEventListener('pointerdown', this.#handleOutsidePointer, true);
    document.addEventListener('keydown', this.#handleDismissKey);
  }

  #stopWatchingForDismiss() {
    document.removeEventListener('pointerdown', this.#handleOutsidePointer, true);
    document.removeEventListener('keydown', this.#handleDismissKey);
  }

  /**
   * @param {PointerEvent} event
   */
  #handleOutsidePointer = (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;

    const { toggle, panel } = this.refs;

    // The toggle has to count as inside, or its own `pointerdown` would close the panel a moment
    // before its `click` reopened it.
    if (toggle instanceof HTMLElement && toggle.contains(target)) return;
    if (panel instanceof HTMLElement && panel.contains(target)) return;

    this.#setPanelExpanded(false);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleDismissKey = (event) => {
    if (event.key !== 'Escape') return;

    this.#setPanelExpanded(false);

    // Focus goes back to the control that opened it, so keyboard users are not dropped at the
    // top of the document.
    const { toggle } = this.refs;
    if (toggle instanceof HTMLElement) toggle.focus();
  };

  /**
   * Toggles one filter group, so groups stay independent without a ref array.
   *
   * `event.target` — not `currentTarget` — is the button carrying the `on:click`. Horizon
   * delegates these handlers from `document` and proxies only `target` onto the bound element,
   * so `currentTarget` is the document by the time this runs.
   *
   * @param {Event} event
   */
  toggleGroup(event) {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!isExpanded));

    // Sort is a one-shot menu rather than a filter group: picking an option closes it, as on live,
    // so its state is deliberately left out and the server's own default applies after a re-render.
    const key = button.dataset.groupKey;
    if (key && key !== SORT_GROUP_KEY) openGroups.set(key, !isExpanded);
  }
}

/** The top tag strip. Same behaviour, its own element name so both can be registered. */
class EqCollectionTopFilter extends EqCollectionFilter {}

if (!customElements.get('eq-collection-filter')) {
  customElements.define('eq-collection-filter', EqCollectionFilter);
}

if (!customElements.get('eq-collection-top-filter')) {
  customElements.define('eq-collection-top-filter', EqCollectionTopFilter);
}

// Pagination is owned by the existing Horizon grid (not the filter control).
// One passive listener for the rendered collection pagination only.
if (!window.__eqCollectionPaginationTrackingInstalled) {
  window.__eqCollectionPaginationTrackingInstalled = true;
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('collection-component results-list nav.pagination a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;
    const href = new URL(link.href, window.location.href);
    const current = Number(link.closest('nav.pagination')?.dataset.current_page || 1);
    const next = href.searchParams.get('page') || String(current);
    const collectionName = document.querySelector('.eq-collection-grid')?.dataset.eqCollectionName || '';
    const tab = document.querySelector('.eq-collection-top-filter__link--active')?.textContent?.trim() || '';
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'collection_pagination', collection_name: collectionName,
      text: link.textContent?.trim() || next, tab,
    });
  }, true);
}
